import React from "react";
import { Button, Switch, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

function Fact({ children }) {
  if (children == null || children === "") return null;
  return <span className="listing-card__fact">{children}</span>;
}

const PLATFORM_OPERATOR_STATUS_TIP =
  "Platform operators can only be enabled/disabled via CLI (scripts/platform_operator_enable.php | platform_operator_disable.php)";

function isPlatformOperatorStaff(staff) {
  if (staff?.isPlatformOperator || staff?.is_platform_operator || staff?.raw?.is_platform_operator) {
    return true;
  }
  const roles = staff?.roles || staff?.rbac_roles || staff?.raw?.rbac_roles || [];
  return roles.some((role) => {
    const name = String(role?.name || role?.role_name || "").toLowerCase();
    return name === "platform_operator";
  });
}

export function StaffListingCard({
  staff,
  loading,
  canManageRoles,
  onToggleStatus,
  onViewProfile,
  onManageRoles,
}) {
  const active = Number(staff?.status) === 1;
  const initials = (staff?.fullName || staff?.email || staff?.staffId || "ST").substring(0, 2).toUpperCase();
  const staffId = staff?.id ?? staff?.raw?.id;
  const platformOperator = isPlatformOperatorStaff(staff);
  const title = staff?.fullName || staff?.email || staff?.staffId || "—";
  const subtitle = staff?.fullName || staff?.email
    ? [staff?.email, staff?.staffId].filter(Boolean).join(' · ')
    : (staff?.department?.name || "No department");

  const statusSwitch = (
    <Switch
      checked={active}
      onChange={(checked) => onToggleStatus(staffId, checked)}
      disabled={loading || platformOperator}
      size="small"
      aria-label={`Toggle status for ${staff?.staffId || staff?.email}`}
    />
  );

  return (
    <article className="listing-card listing-card--linear staff-listing-card">
      <div className="listing-card__lead">
        <div className="listing-card__icon is-staff" aria-hidden>
          {initials}
        </div>
        <div className="listing-card__identity">
          <h3 className="listing-card__title">{title}</h3>
          <p className="listing-card__meta">{subtitle}</p>
        </div>
      </div>

      <div className="listing-card__facts">
        <Fact>{staff?.primaryRoleName || "Unassigned"}</Fact>
        <Fact>{staff?.department?.name}</Fact>
        <Fact>{staff?.email && staff?.fullName ? staff.email : null}</Fact>
        <Fact>{staff?.staffId && (staff?.fullName || staff?.email) ? `ID: ${staff.staffId}` : null}</Fact>
        {platformOperator ? <Fact>Platform operator</Fact> : null}
      </div>

      <div className="listing-card__aside" onClick={(e) => e.stopPropagation()}>
        <span className={`listing-card__status ${active ? "is-active" : "is-inactive"}`}>
          {active ? <CheckOutlined /> : <CloseOutlined />}
          {active ? "Active" : "Inactive"}
        </span>
        {platformOperator ? (
          <Tooltip title={PLATFORM_OPERATOR_STATUS_TIP}>{statusSwitch}</Tooltip>
        ) : (
          statusSwitch
        )}
        <Button type="link" size="small" className="listing-card__action" onClick={() => onViewProfile(staff)}>
          Profile
        </Button>
        <Button
          type="link"
          size="small"
          className="listing-card__action"
          onClick={() => onManageRoles(staff)}
          disabled={!canManageRoles}
        >
          Roles
        </Button>
      </div>
    </article>
  );
}

export default StaffListingCard;
